/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Edit2, ArrowUpRight, ArrowDownRight, Calculator, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/accounting`;

const EXPENSE_CATEGORIES = ['Yanacaq', 'Material', 'Ofis', 'Nəqliyyat', 'Digər'];

function fmt(n) {
  const num = Number(n) || 0;
  return num.toLocaleString('az-AZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₼';
}

export default function CashKassirsTab() {
  const [subTab, setSubTab] = useState('kassirs'); // kassirs | entries | reconciliation
  const [kassirs, setKassirs] = useState([]);
  const [collections, setCollections] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [reconciliation, setReconciliation] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);

  // Modal states
  const [kassirModalOpen, setKassirModalOpen] = useState(false);
  const [editingKassir, setEditingKassir] = useState(null);
  const [collectionModalOpen, setCollectionModalOpen] = useState(false);
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [reconModalOpen, setReconModalOpen] = useState(false);
  const [editingRecon, setEditingRecon] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchKassirs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/kassirs`, { headers });
      setKassirs(r.data || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchEntries = useCallback(async () => {
    try {
      const [c, e] = await Promise.all([
        axios.get(`${API}/cash-collections?month=${selectedMonth}`, { headers }),
        axios.get(`${API}/cash-expenses?month=${selectedMonth}`, { headers }),
      ]);
      setCollections(c.data || []);
      setExpenses(e.data || []);
    } catch (err) { console.error(err); }
  }, [selectedMonth]);

  const fetchReconciliation = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/kassir-reconciliation?month=${selectedMonth}`, { headers });
      setReconciliation(r.data);
    } catch (e) { console.error(e); }
  }, [selectedMonth]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchKassirs(), fetchEntries(), fetchReconciliation()]);
    setLoading(false);
  }, [fetchKassirs, fetchEntries, fetchReconciliation]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return <div className="p-8 text-center text-gray-500">Yüklənir...</div>;

  return (
    <div className="space-y-4" data-testid="cash-kassirs-tab">
      {/* Month selector */}
      <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg border">
        <label className="text-sm font-medium">Hesabat ayı:</label>
        <Input
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="w-44"
          data-testid="cash-month-select"
        />
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b pb-2">
        <Button variant={subTab === 'kassirs' ? 'default' : 'outline'} onClick={() => setSubTab('kassirs')} data-testid="cash-subtab-kassirs">
          <Users className="w-4 h-4 mr-1" /> Kassirlər
        </Button>
        <Button variant={subTab === 'entries' ? 'default' : 'outline'} onClick={() => setSubTab('entries')} data-testid="cash-subtab-entries">
          Yığım & Xərc
        </Button>
        <Button variant={subTab === 'reconciliation' ? 'default' : 'outline'} onClick={() => setSubTab('reconciliation')} data-testid="cash-subtab-reconciliation">
          <Calculator className="w-4 h-4 mr-1" /> Ay Sonu Müqayisə
        </Button>
      </div>

      {subTab === 'kassirs' && (
        <KassirsList
          kassirs={kassirs}
          onAdd={() => { setEditingKassir(null); setKassirModalOpen(true); }}
          onEdit={(k) => { setEditingKassir(k); setKassirModalOpen(true); }}
          onDelete={async (id) => {
            if (!window.confirm('Kassir deaktiv edilsin?')) return;
            try {
              await axios.delete(`${API}/kassirs/${id}`, { headers });
              toast.success('Deaktiv edildi');
              fetchKassirs();
            } catch (e) { toast.error('Xəta: ' + (e.response?.data?.detail || e.message)); }
          }}
        />
      )}

      {subTab === 'entries' && (
        <EntriesList
          collections={collections}
          expenses={expenses}
          onAddCollection={() => setCollectionModalOpen(true)}
          onAddExpense={() => setExpenseModalOpen(true)}
          onDeleteCollection={async (id) => {
            if (!window.confirm('Silinsin?')) return;
            try { await axios.delete(`${API}/cash-collections/${id}`, { headers }); toast.success('Silindi'); fetchEntries(); }
            catch (e) { toast.error('Xəta'); }
          }}
          onDeleteExpense={async (id) => {
            if (!window.confirm('Silinsin?')) return;
            try { await axios.delete(`${API}/cash-expenses/${id}`, { headers }); toast.success('Silindi'); fetchEntries(); }
            catch (e) { toast.error('Xəta'); }
          }}
        />
      )}

      {subTab === 'reconciliation' && reconciliation && (
        <ReconciliationView
          reconciliation={reconciliation}
          month={selectedMonth}
          onEdit={(row) => { setEditingRecon(row); setReconModalOpen(true); }}
        />
      )}

      {/* Modals */}
      <KassirModal
        open={kassirModalOpen}
        onClose={() => setKassirModalOpen(false)}
        editing={editingKassir}
        onSaved={() => { fetchKassirs(); setKassirModalOpen(false); }}
      />
      <CollectionModal
        open={collectionModalOpen}
        onClose={() => setCollectionModalOpen(false)}
        kassirs={kassirs}
        onSaved={() => { fetchEntries(); setCollectionModalOpen(false); }}
      />
      <ExpenseModal
        open={expenseModalOpen}
        onClose={() => setExpenseModalOpen(false)}
        onSaved={() => { fetchEntries(); setExpenseModalOpen(false); }}
      />
      <ReconModal
        open={reconModalOpen}
        onClose={() => setReconModalOpen(false)}
        row={editingRecon}
        month={selectedMonth}
        onSaved={() => { fetchReconciliation(); setReconModalOpen(false); }}
      />
    </div>
  );
}

function KassirsList({ kassirs, onAdd, onEdit, onDelete }) {
  return (
    <div className="bg-white rounded-xl border shadow-sm">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <h3 className="font-semibold">Kassirlər ({kassirs.length})</h3>
        <Button onClick={onAdd} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-kassir-btn">
          <Plus className="w-4 h-4 mr-1" /> Yeni kassir
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-3 py-2">Ad</th>
              <th className="text-left px-3 py-2">Region</th>
              <th className="text-left px-3 py-2">Telefon</th>
              <th className="text-left px-3 py-2">Qeyd</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {kassirs.length === 0 && (
              <tr><td colSpan="5" className="text-center py-6 text-gray-400">Hələ kassir yoxdur</td></tr>
            )}
            {kassirs.map(k => (
              <tr key={k.id} className="border-t hover:bg-gray-50" data-testid={`kassir-row-${k.id}`}>
                <td className="px-3 py-2 font-medium">{k.name}</td>
                <td className="px-3 py-2">{k.region || '-'}</td>
                <td className="px-3 py-2">{k.phone || '-'}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{k.note || '-'}</td>
                <td className="px-3 py-2 text-right space-x-2">
                  <button onClick={() => onEdit(k)} className="text-blue-600 hover:text-blue-800" data-testid={`edit-kassir-${k.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => onDelete(k.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-kassir-${k.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EntriesList({ collections, expenses, onAddCollection, onAddExpense, onDeleteCollection, onDeleteExpense }) {
  const totalCollections = collections.reduce((s, c) => s + c.amount, 0);
  const totalExpenses = expenses.reduce((s, e) => s + e.amount, 0);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-emerald-700">Cəm yığım</p>
              <p className="text-2xl font-bold text-emerald-800">{fmt(totalCollections)}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-emerald-600" />
          </div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-orange-700">Cəm xərc</p>
              <p className="text-2xl font-bold text-orange-800">{fmt(totalExpenses)}</p>
            </div>
            <TrendingDown className="w-8 h-8 text-orange-600" />
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-blue-700">Xalis qalıq</p>
              <p className="text-2xl font-bold text-blue-800">{fmt(totalCollections - totalExpenses)}</p>
            </div>
            <Calculator className="w-8 h-8 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Collections */}
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2"><ArrowUpRight className="w-4 h-4 text-emerald-600" /> Yığımlar ({collections.length})</h3>
          <Button size="sm" onClick={onAddCollection} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-collection-btn">
            <Plus className="w-4 h-4 mr-1" /> Yığım əlavə et
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Tarix</th>
                <th className="text-left px-3 py-2">Kassir</th>
                <th className="text-right px-3 py-2">Məbləğ</th>
                <th className="text-left px-3 py-2">Qeyd</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {collections.length === 0 && (
                <tr><td colSpan="5" className="text-center py-6 text-gray-400">Hələ yığım qeydi yoxdur</td></tr>
              )}
              {collections.map(c => (
                <tr key={c.id} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">{c.date}</td>
                  <td className="px-3 py-2">{c.kassir_name}</td>
                  <td className="px-3 py-2 text-right font-semibold text-emerald-700">{fmt(c.amount)}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{c.note || '-'}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => onDeleteCollection(c.id)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expenses */}
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2"><ArrowDownRight className="w-4 h-4 text-orange-600" /> Xərclər ({expenses.length})</h3>
          <Button size="sm" onClick={onAddExpense} variant="outline" className="border-orange-300 text-orange-700" data-testid="add-expense-btn">
            <Plus className="w-4 h-4 mr-1" /> Xərc əlavə et
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Tarix</th>
                <th className="text-left px-3 py-2">Kateqoriya</th>
                <th className="text-left px-3 py-2">Alıcı</th>
                <th className="text-right px-3 py-2">Məbləğ</th>
                <th className="text-left px-3 py-2">Təsvir</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 && (
                <tr><td colSpan="6" className="text-center py-6 text-gray-400">Hələ xərc qeydi yoxdur</td></tr>
              )}
              {expenses.map(e => (
                <tr key={e.id} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">{e.date}</td>
                  <td className="px-3 py-2">
                    <span className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded">{e.category}</span>
                  </td>
                  <td className="px-3 py-2">{e.recipient || '-'}</td>
                  <td className="px-3 py-2 text-right font-semibold text-orange-700">{fmt(e.amount)}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{e.description || '-'}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => onDeleteExpense(e.id)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ReconciliationView({ reconciliation, month, onEdit }) {
  const { rows, summary } = reconciliation;
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="px-4 py-3 border-b">
          <h3 className="font-semibold">{month} Reconciliation</h3>
          <p className="text-xs text-gray-500 mt-1">
            Müsbət fərq (+) = kassir bizə borcludur. Mənfi (−) = bizdə artıq qalıb.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Kassir</th>
                <th className="text-right px-3 py-2">Bizə verdi</th>
                <th className="text-right px-3 py-2">Billing → Наличные</th>
                <th className="text-right px-3 py-2">Billing → Qoşulma haqqı</th>
                <th className="text-right px-3 py-2">Billing CƏM</th>
                <th className="text-right px-3 py-2">Fərq</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="7" className="text-center py-6 text-gray-400">Bu ay üçün kassir yoxdur</td></tr>
              )}
              {rows.map(r => (
                <tr key={r.kassir_id} className="border-t" data-testid={`recon-row-${r.kassir_id}`}>
                  <td className="px-3 py-2 font-medium">{r.kassir_name}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.given_to_us)}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.billing_cash_total)}</td>
                  <td className="px-3 py-2 text-right">{fmt(r.billing_connection_fee_total)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmt(r.billing_total)}</td>
                  <td className={`px-3 py-2 text-right font-bold ${
                    r.diff > 0.01 ? 'text-red-600' : r.diff < -0.01 ? 'text-amber-600' : 'text-emerald-600'
                  }`}>
                    {r.diff > 0 ? '+' : ''}{fmt(r.diff)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => onEdit(r)} data-testid={`edit-recon-${r.kassir_id}`}>
                      Billing daxil et
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 bg-gray-50 font-bold">
                <td className="px-3 py-2">CƏM</td>
                <td className="px-3 py-2 text-right">{fmt(summary.total_given_to_us)}</td>
                <td className="px-3 py-2 text-right">{fmt(summary.total_billing_cash)}</td>
                <td className="px-3 py-2 text-right">{fmt(summary.total_billing_connection_fee)}</td>
                <td className="px-3 py-2 text-right">{fmt(summary.total_billing)}</td>
                <td className={`px-3 py-2 text-right ${summary.total_diff > 0.01 ? 'text-red-600' : summary.total_diff < -0.01 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {summary.total_diff > 0 ? '+' : ''}{fmt(summary.total_diff)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Bizə verilən" value={summary.total_given_to_us} color="emerald" />
        <SummaryCard label="Billing CƏM" value={summary.total_billing} color="purple" />
        <SummaryCard label="Cəm xərclər" value={summary.total_expenses} color="orange" />
        <SummaryCard label="Xalis nağd" value={summary.net_cash} color="blue" highlight />
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color, highlight }) {
  const colors = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[color]} ${highlight ? 'ring-2 ring-offset-2 ring-blue-300' : ''}`}>
      <p className="text-xs">{label}</p>
      <p className="text-lg font-bold">{fmt(value)}</p>
    </div>
  );
}

// ====== MODALS ======
function KassirModal({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState({ name: '', phone: '', region: '', note: '' });
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (editing) setForm({ name: editing.name || '', phone: editing.phone || '', region: editing.region || '', note: editing.note || '' });
    else setForm({ name: '', phone: '', region: '', note: '' });
  }, [editing, open]);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Ad tələb olunur'); return; }
    try {
      const h = { headers: { Authorization: `Bearer ${token}` } };
      if (editing) await axios.put(`${API}/kassirs/${editing.id}`, form, h);
      else await axios.post(`${API}/kassirs`, form, h);
      toast.success(editing ? 'Yeniləndi' : 'Əlavə edildi');
      onSaved();
    } catch (e) { toast.error('Xəta: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? 'Kassiri redaktə et' : 'Yeni kassir'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm">Ad</label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="kassir-name-input" />
          </div>
          <div>
            <label className="text-sm">Region</label>
            <Input value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} />
          </div>
          <div>
            <label className="text-sm">Telefon</label>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <label className="text-sm">Qeyd</label>
            <Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Ləğv</Button>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="kassir-save-btn">Yadda saxla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CollectionModal({ open, onClose, kassirs, onSaved }) {
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), kassir_id: '', amount: '', note: '' });
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (open) setForm({ date: new Date().toISOString().slice(0, 10), kassir_id: '', amount: '', note: '' });
  }, [open]);

  const save = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error('Məbləğ müsbət olmalıdır'); return; }
    if (!form.kassir_id) { toast.error('Kassir seçin'); return; }
    try {
      await axios.post(`${API}/cash-collections`, { ...form, amount }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Yığım qeyd edildi');
      onSaved();
    } catch (e) { toast.error('Xəta: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Yığım əlavə et</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm">Kassir</label>
            <Select value={form.kassir_id} onValueChange={v => setForm({ ...form, kassir_id: v })}>
              <SelectTrigger data-testid="collection-kassir-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                {kassirs.map(k => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm">Tarix</label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <label className="text-sm">Məbləğ (₼)</label>
              <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="collection-amount-input" />
            </div>
          </div>
          <div>
            <label className="text-sm">Qeyd</label>
            <Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Ləğv</Button>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="collection-save-btn">Yadda saxla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExpenseModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), category: 'Yanacaq', amount: '', recipient: '', description: '' });
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (open) setForm({ date: new Date().toISOString().slice(0, 10), category: 'Yanacaq', amount: '', recipient: '', description: '' });
  }, [open]);

  const save = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error('Məbləğ müsbət olmalıdır'); return; }
    try {
      await axios.post(`${API}/cash-expenses`, { ...form, amount }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Xərc qeyd edildi');
      onSaved();
    } catch (e) { toast.error('Xəta: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Xərc əlavə et</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm">Tarix</label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <label className="text-sm">Məbləğ (₼)</label>
              <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount-input" />
            </div>
          </div>
          <div>
            <label className="text-sm">Kateqoriya</label>
            <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {EXPENSE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm">Alıcı</label>
            <Input value={form.recipient} onChange={e => setForm({ ...form, recipient: e.target.value })} />
          </div>
          <div>
            <label className="text-sm">Təsvir</label>
            <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Ləğv</Button>
          <Button onClick={save} className="bg-orange-600 hover:bg-orange-700" data-testid="expense-save-btn">Yadda saxla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReconModal({ open, onClose, row, month, onSaved }) {
  const [form, setForm] = useState({ billing_cash_total: '', billing_connection_fee_total: '' });
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (row) setForm({
      billing_cash_total: String(row.billing_cash_total || 0),
      billing_connection_fee_total: String(row.billing_connection_fee_total || 0),
    });
  }, [row, open]);

  const save = async () => {
    try {
      await axios.post(`${API}/kassir-reconciliation`, {
        month,
        kassir_id: row.kassir_id,
        billing_cash_total: parseFloat(form.billing_cash_total) || 0,
        billing_connection_fee_total: parseFloat(form.billing_connection_fee_total) || 0,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Yeniləndi');
      onSaved();
    } catch (e) { toast.error('Xəta: ' + (e.response?.data?.detail || e.message)); }
  };

  if (!row) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{row.kassir_name} — {month} Billing daxil et</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm">Billing → Наличные (nağd abonentlik)</label>
            <Input type="number" step="0.01" value={form.billing_cash_total} onChange={e => setForm({ ...form, billing_cash_total: e.target.value })} data-testid="recon-cash-input" />
          </div>
          <div>
            <label className="text-sm">Billing → Плата за подключение (Qoşulma haqqı)</label>
            <Input type="number" step="0.01" value={form.billing_connection_fee_total} onChange={e => setForm({ ...form, billing_connection_fee_total: e.target.value })} data-testid="recon-conn-input" />
          </div>
          <p className="text-xs text-gray-500">
            Bizə verdi: <strong>{fmt(row.given_to_us)}</strong> — bu məbləğlər billing-dən manual götürülür.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Ləğv</Button>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="recon-save-btn">Yadda saxla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
